import React, { useEffect, useState } from 'react';

import { Web } from '@mui/icons-material';
import { Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { buildCoverFields } from './coverData';
import downloadPayrollVerificationPdf from './downloadPayrollVerificationPdf';
import { PayrollVerificationForm } from './Form';
import { buildReport, emptyPeriodMessage } from './monthly';
import WorkerSummary from './WorkerSummary';
import PDF from '../../../assets/pdf.png';
import { Breadcrumbs, Button, EdgeReportingWindow, SubHeader, ReportLoader } from '../../../components';
import ImageColorChanger from '../../../utils/imageColorChanger';
import getEdgeReportingMenu from '../getEdgeReportingMenu';
import { userHasAccess, generateReport, getReportData, removeReportData } from '../operations';

import './styles.scoped.css';

// PRST-134 — Payroll Verification ("Earnings History"), the packet Corporate Payroll sends in
// response to a subpoena for a worker's payroll records.
//
// Unlike every other Edge report this one does not end in a table: the results screen is a single
// worker's cover data for the user to eyeball, and the deliverable is a multi-page PDF (PDF
// specifically, "to prevent alterations to the data provided").
//
// Same report on both brands; the proc derives Company from the database it runs against, so the
// payload carries no brand.
export const REPORT_ENDPOINT = 'payroll-verification-report';

export const initialForm = {
  EmployeeId: '',
  SSNO: '',
  FromPerEnddate: '',
  ToPerEnddate: '',
};

// A worker is identified by ID or by SSN, never both — the proc filters on whichever is supplied
// and two different identifiers could name two different people.
const SSN_PATTERN = /^\d{3}-?\d{2}-?\d{4}$/;

// Validate on press (SOP: no disabled gating, no alert); returns { fieldName: message }.
export const validateCriteria = (form = {}) => {
  const errors = {};
  const employeeId = String(form.EmployeeId ?? '').trim();
  const ssn = String(form.SSNO ?? '').trim();

  if (!employeeId && !ssn) {
    errors.WorkerIdentity = 'Enter either a Worker Number or a Worker SSN.';
  } else if (employeeId && ssn) {
    errors.WorkerIdentity = 'Enter a Worker Number or a Worker SSN, not both.';
  }
  if (employeeId && !/^\d+$/.test(employeeId)) {
    errors.EmployeeId = 'Worker Number must contain numbers only.';
  }
  if (ssn && !SSN_PATTERN.test(ssn)) {
    errors.SSNO = 'Worker SSN must be 9 digits, for example 000-00-0000.';
  }
  if (!form.FromPerEnddate || !form.ToPerEnddate) {
    errors.PeriodRequestedDates = 'Period Requested Dates is required';
  } else if (form.ToPerEnddate < form.FromPerEnddate) {
    errors.PeriodRequestedDates = 'The To date must be on or after the From date.';
  }
  return errors;
};

const PayrollVerificationCriteria = ({ form, handleChange, handleViewResults, errors, loadingResults }) => {
  const navigate = useNavigate();
  return (
    <Form
      noValidate
      onSubmit={e => e.preventDefault()}
      style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
      <PayrollVerificationForm form={form} handleChange={handleChange} errors={errors} />
      <div className="buttons-row">
        {/* Per SOP, keep the action enabled and validate on press (inline field errors);
            reserve disabled for the in-flight state only. */}
        <Button
          variant="primary"
          disabled={loadingResults}
          onPress={() => handleViewResults()}
          style={{ marginRight: '20px', width: '100px' }}
          text="View"
        />
        <Button
          style={{ width: '100px' }}
          onPress={() => navigate('/edge-reporting')}
          variant="outline"
          text="Cancel"
        />
      </div>
    </Form>
  );
};

const PayrollVerification = () => {
  const { t } = useTranslation();
  const { menuItems } = getEdgeReportingMenu(t);
  const { user } = useSelector(state => state.admin);
  const navigate = useNavigate();
  const [form, setForm] = useState(initialForm);
  const [report, setReport] = useState(null);
  const [loadingResults, setLoadingResults] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (menuItems) {
      if (!userHasAccess(user, menuItems.find(i => i.to === window.location.pathname)?.id)) navigate('/');
    }
  }, [menuItems]);

  const handleChange = e => {
    const { name, value } = e.target;
    // Clear a field's validation error as soon as the user edits it. Either identifier clears the
    // shared "one or the other" error, since editing either one can resolve it.
    const errorKeys = ['FromPerEnddate', 'ToPerEnddate'].includes(name) ? ['PeriodRequestedDates'] : [name];
    if (['EmployeeId', 'SSNO'].includes(name)) errorKeys.push('WorkerIdentity');
    setErrors(prev => {
      const next = { ...prev };
      errorKeys.forEach(key => delete next[key]);
      return next;
    });
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleViewResults = async () => {
    const nextErrors = validateCriteria(form);
    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      return;
    }
    setErrors({});
    setLoadingResults(true);

    // Criteria map 1:1 to sp_cl_cp_bi_pv params. The SSN key MUST stay spelled `SSNO`: the report
    // worker obfuscates SSNs in CloudWatch by checking that exact field name, and any other
    // spelling silently logs the number in full.
    const payload = {
      EmployeeId: Number(form.EmployeeId) || 0,
      SSNO: String(form.SSNO ?? '').trim(),
      FromPerEnddate: form.FromPerEnddate,
      ToPerEnddate: form.ToPerEnddate,
    };

    try {
      const result = await generateReport({ endpoint: REPORT_ENDPOINT, payload });
      const rows = result?.location ? await getReportData(result.location) : [];
      if (result?.location) await removeReportData(result.location);

      // getReportData returns undefined on a fetch/parse failure vs [] for a genuinely empty
      // result — don't report a failure to the user as "no data found".
      if (rows === undefined) {
        alert('An error occurred while generating the report. Please try again.');
      } else if (rows?.length) {
        const built = buildReport(rows);
        if (!built.cover) {
          alert('No worker record was found matching your criteria.');
        } else {
          // The business requires the checks and the totals to agree exactly. They are not equal by
          // construction — gross/net are check-header columns while the totals are summed from a
          // different table — so a mismatch is surfaced rather than silently printed into a legal
          // document. It does not block: a proc bug must be visible in UAT, not a blank screen.
          const mismatched = built.tieOut.filter(month => !month.grossOk || !month.netOk);
          if (mismatched.length) {
            alert(
              `Warning: the check totals and the summarized amounts do not reconcile for ` +
                `${mismatched.map(m => m.key).join(', ')}. Please report this before sending the PDF.`,
            );
          }
          setReport(built);
          setShowResults(true);
        }
      } else {
        alert(result?.statusDescription || 'No data found matching your criteria.');
      }
    } catch (error) {
      console.log(error);
      alert('An error occurred while generating the report. Please try again.');
    } finally {
      setLoadingResults(false);
    }
  };

  const handleShowPdf = async () => {
    if (!report) return;
    setLoadingResults(true);
    try {
      // react-pdf lays out synchronously on the main thread with no yielding between pages, so a
      // multi-year packet freezes the tab for seconds. Yield one macrotask first so the spinner
      // actually paints before that happens.
      await new Promise(resolve => setTimeout(resolve, 0));
      await downloadPayrollVerificationPdf({
        criteria: { FromPerEnddate: form.FromPerEnddate, ToPerEnddate: form.ToPerEnddate },
        cover: report.cover,
        months: report.months,
        reportDate: report.reportDate,
      });
    } catch (error) {
      console.log(error);
      alert('An error occurred while building the PDF. Please try again.');
    } finally {
      setLoadingResults(false);
    }
  };

  return (
    <>
      <SubHeader
        text="EDGE Reporting"
        buttons={[{ icon: <Web sx={{ color: '#fff', fontSize: 30 }} />, onPress: () => navigate('/dashboard') }]}
        popoverTitle="Need help with your Payroll Verification Report?"
      />
      <div className="header-row">
        <Breadcrumbs
          leftTitle="Reporting Home"
          middleTitle={showResults && 'Payroll Verification'}
          midItemAction={() => setShowResults(false)}
          rightTitle={showResults ? 'Viewing Report' : 'Payroll Verification'}
          route="/edge-reporting"
        />
        {showResults && (
          <div className="export-links">
            {/* Per the mockup the export is a red PDF icon at the right end of the breadcrumb
                row — the same position and treatment as every other Edge report's CSV icon —
                not a labelled "Show PDF" button. */}
            <button
              type="button"
              className="export-icon-button"
              disabled={loadingResults}
              onClick={handleShowPdf}
              aria-label="Export the payroll verification report to PDF">
              <ImageColorChanger src={PDF} className="export-icon" alt="export-payroll-verification-to-pdf" />
            </button>
          </div>
        )}
      </div>
      {loadingResults && <ReportLoader didAbort={() => setLoadingResults(false)} />}
      {showResults ? (
        <>
          {/* Verbatim from the mockup, which reads "If", not the ticket body's "IF". */}
          <div className="summary-message">
            Review the details of this worker. If it looks correct, click on the PDF button to export the
            report.
          </div>
          {/* PRST-140: zero checks in range is a legitimate result the proc cannot flag (COVER is
              built independently of the checks), and without this line it is indistinguishable
              from a complete packet. Warn, don't block — same rule as the tie-out above; the PDF
              cover carries the same sentence. */}
          {report?.months?.length === 0 && (
            <div className="empty-period-message" role="alert">
              {emptyPeriodMessage({ FromPerEnddate: form.FromPerEnddate, ToPerEnddate: form.ToPerEnddate })}
            </div>
          )}
          <WorkerSummary fields={buildCoverFields(report?.cover, report?.reportDate)} />
          <div className="buttons-row">
            <Button
              variant="primary"
              onPress={() => setShowResults(false)}
              style={{ marginRight: '20px', width: '100px' }}
              text="Back"
            />
            <Button
              style={{ width: '100px' }}
              onPress={() => navigate('/edge-reporting')}
              variant="outline"
              text="Cancel"
            />
          </div>
        </>
      ) : (
        <EdgeReportingWindow
          containerStyle={{ border: '2px solid #343a40', minHeight: 200 }}
          windowContent={
            <PayrollVerificationCriteria
              form={form}
              handleChange={handleChange}
              handleViewResults={handleViewResults}
              errors={errors}
              loadingResults={loadingResults}
            />
          }
          windowTitle="Criteria"
          headerStyle={{ backgroundColor: '#343a40' }}
        />
      )}
    </>
  );
};

export default PayrollVerification;
